import {
  ReportSectionItemTable,
  type ReportSectionItemTableProps,
  type ReportTableCell,
} from '@/components';
import { getEmptySectionTableDataByHeader } from '@/features/mold/utils';
import type { PaymentAmountBreakdownTableData } from '@/features/project/_utils/payment';
import { formatValueByUnit } from '@/utils';
import { SPACE_TEXT } from '@/utils/constants';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  paymentAmountBreakdownTableData: PaymentAmountBreakdownTableData;
};

export const AmountBreakdownTable: FC<Props> = ({
  paymentAmountBreakdownTableData,
}) => {
  const { t } = useTranslation();

  const amountBreakdownSectionTableData = useMemo<{
    header: ReportSectionItemTableProps['header'];
    data: ReportSectionItemTableProps['data'];
  }>(() => {
    const header: ReportSectionItemTableProps['header'] = [
      {
        type: 'text',
        value: SPACE_TEXT,
        ratio: 0.3,
      },
      { type: 'text', value: t('payment-name'), ratio: 2 },
      { type: 'text', value: t('payment-contract-overview'), ratio: 1.5 },
      { type: 'text', value: t('payment-amount-breakdown-name'), ratio: 1 },
      { type: 'text', value: t('payment-amount-breakdown-purpose'), ratio: 2 },
      {
        type: 'text',
        value: t('payment-amount-breakdown-amount'),
        align: 'center',
        ratio: 1,
      },
    ];

    const data: ReportSectionItemTableProps['data'] =
      paymentAmountBreakdownTableData.flatMap((data) => {
        const { children, ...resetData } = data;
        const createRow = (d: Partial<typeof resetData>) => ({
          row: [
            {
              type: 'text',
              value: d.displayCode,
              align: 'center',
            },
            { type: 'text', value: d.paymentName },
            { type: 'text', value: d.contractOverview },
            { type: 'text', value: d.name },
            { type: 'text', value: d.purpose },
            {
              type: 'text',
              value: formatValueByUnit(d.amount, {
                operation: 'round',
                unit: 'thousand',
              }),
              align: 'right',
            },
          ] as ReportTableCell[],
        });
        return [createRow(resetData), ...(children ?? []).map(createRow)];
      });

    return {
      header,
      data: data.length ? data : getEmptySectionTableDataByHeader(header),
    };
  }, [paymentAmountBreakdownTableData, t]);

  return (
    <ReportSectionItemTable
      header={amountBreakdownSectionTableData.header}
      data={amountBreakdownSectionTableData.data}
    />
  );
};
